import { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { AuthContext, AuthContextType, FeatureFlagContext } from "./customHooks";
import { getFeatureFlags, updateFeatureFlags as updateFeatureFlagsApi } from "../services/adminService";
import { getRegionSettings } from "../services/admin/regionService";
import { isAdminLevelRole } from "./regionVisibility";
import * as T from "./frontendTypes";

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const auth = useContext(AuthContext) as AuthContextType;
  // Includes auditor: flag-gated surfaces (testTools, dueDiligenceAgent) must load for auditors too
  const isAdmin = isAdminLevelRole(auth?.userRole);

  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [registry, setRegistry] = useState<T.FeatureFlagRegistry>({});
  const [fullPortalEnabled, setFullPortalEnabled] = useState(true);
  const [embeddedSigningEnabled, setEmbeddedSigningEnabled] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  const refreshFlags = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [flagsRes, settingsRes] = await Promise.all([
        getFeatureFlags(),
        getRegionSettings(),
      ]);
      if (flagsRes.success) {
        setFlags(flagsRes.flags);
        setRegistry(flagsRes.registry);
      }
      if (settingsRes.success) {
        setFullPortalEnabled(settingsRes.data.fullPortalEnabled ?? true);
        setEmbeddedSigningEnabled(settingsRes.data.embeddedSigningEnabled ?? true);
      }
      setIsLoaded(true);
    } catch {
      // On failure, keep defaults (all visible)
      setIsLoaded(true);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && auth.isAuthInitialized) {
      refreshFlags();
    }
  }, [isAdmin, auth.isAuthInitialized, refreshFlags]);

  const updateFlags = useCallback(async (updates: Record<string, boolean>): Promise<T.FeatureFlagsResponse> => {
    const res = await updateFeatureFlagsApi(updates);
    if (res.success) {
      setFlags(res.flags);
    }
    return res;
  }, []);

  return (
    <FeatureFlagContext.Provider value={{ flags, registry, isLoaded, fullPortalEnabled, embeddedSigningEnabled, refreshFlags, updateFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}
